import { useLiveMapData } from './hooks/useLiveMapData'
import { useMapDataSnapshot } from './hooks/useMapDataSnapshot'
import { useMapPauseState } from './hooks/useMapPauseState'
import Map from './parts/Map'
import { ColorEnum } from '@/constants/color'
import { Layout } from '@/features/layout/types'
import { logger } from '@/logger'
import { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActivityIndicator, StyleSheet, View } from 'react-native'

interface ContentProps {
    hidden: boolean
    layout: Layout
    onOrderPress: (id: string) => void
}

const Content = ({ hidden, layout, onOrderPress }: ContentProps) => {
    const { i18n, t } = useTranslation()

    const liveData = useLiveMapData()
    const paused = useMapPauseState(hidden, layout)
    const dataSnapshot = useMapDataSnapshot(liveData, paused)

    const [mapError, setMapError] = useState(false)
    const [mapLoaded, setMapLoaded] = useState(false)

    const onLoad = useCallback(() => {
        setMapLoaded(true)
        setMapError(false)
    }, [])
    const onError = useCallback((error?: unknown) => {
        logger.errorOnly(error ?? new Error('Orders map failed to load'))
        setMapError(true)
    }, [])

    const shouldMountMap = !!dataSnapshot

    return (
        <View
            style={styles.container}
            pointerEvents={paused ? 'none' : 'auto'}
            accessibilityElementsHidden={paused}
            importantForAccessibility={paused ? 'no-hide-descendants' : 'auto'}
        >
            {shouldMountMap && (
                <Map
                    venue={dataSnapshot.venue}
                    orders={dataSnapshot.orders}
                    language={i18n.resolvedLanguage ?? i18n.language}
                    paused={paused}
                    onOrderPress={onOrderPress}
                    onLoad={onLoad}
                    onError={onError}
                />
            )}
            {shouldMountMap && !mapLoaded && !mapError && (
                <View pointerEvents="none" style={styles.loading}>
                    <ActivityIndicator
                        size="large"
                        color={ColorEnum.BLUE_500}
                        accessibilityLabel={t('ordersMap.loading')}
                    />
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    container: { flex: 1, overflow: 'hidden', backgroundColor: ColorEnum.GRAY_100 },
    loading: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center',
        justifyContent: 'center',
    },
})

export default memo(Content)
